/*
 * <plp-view-toggle> — grid density switch for the collection page.
 *
 * Two states, each controlling desktop AND mobile column counts together:
 *   - "comfortable" → desktop 3 cols + mobile 1 col
 *   - "compact"     → desktop 4 cols + mobile 2 cols
 *
 * The chosen state is persisted in localStorage and re-applied after the
 * facet form swaps the product grid (AJAX filtering / infinite scroll).
 */
if (!customElements.get('plp-view-toggle')) {
  customElements.define(
    'plp-view-toggle',
    class PlpViewToggle extends HTMLElement {
      static STORAGE_KEY = 'plp-view-density';

      static STATES = {
        comfortable: { desktop: 3, mobile: 1 },
        compact: { desktop: 4, mobile: 2 },
      };

      connectedCallback() {
        this.buttons = Array.from(this.querySelectorAll('[data-state]'));
        this.grid =
          document.getElementById('ProductGridContainer')?.querySelector('.card-grid') || null;
        this.gridContainer = document.getElementById('ProductGridContainer');

        this.state = this.resolveInitialState();
        this.apply();

        this.buttons.forEach((btn) => {
          btn.addEventListener('click', () => this.setState(btn.dataset.state));
        });

        // Re-apply after the grid is replaced by AJAX filtering.
        if (this.gridContainer) {
          this.observer = new MutationObserver(() => {
            const grid = this.gridContainer.querySelector('.card-grid');
            if (grid && grid !== this.grid) {
              this.grid = grid;
              this.applyToGrid();
            }
          });
          this.observer.observe(this.gridContainer, { childList: true, subtree: true });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      resolveInitialState() {
        let stored;
        try {
          stored = localStorage.getItem(PlpViewToggle.STORAGE_KEY);
        } catch (e) {
          stored = null;
        }
        if (stored && PlpViewToggle.STATES[stored]) return stored;

        // Fall back to the theme setting (default desktop columns).
        return Number(this.dataset.default) >= 4 ? 'compact' : 'comfortable';
      }

      setState(state) {
        if (!PlpViewToggle.STATES[state] || state === this.state) return;
        this.state = state;
        try {
          localStorage.setItem(PlpViewToggle.STORAGE_KEY, state);
        } catch (e) {
          /* storage unavailable — ignore */
        }
        this.apply();
      }

      apply() {
        this.applyToGrid();
        this.buttons.forEach((btn) => {
          btn.setAttribute('aria-pressed', String(btn.dataset.state === this.state));
        });
      }

      applyToGrid() {
        if (!this.grid) {
          this.grid = document.getElementById('ProductGridContainer')?.querySelector('.card-grid');
        }
        if (!this.grid) return;

        const { desktop, mobile } = PlpViewToggle.STATES[this.state];
        const classes = Array.from(this.grid.classList);
        classes.forEach((cls) => {
          if (/^card-grid--\d$/.test(cls) || /^mobile:card-grid--\d$/.test(cls)) {
            this.grid.classList.remove(cls);
          }
        });
        this.grid.classList.add(`card-grid--${desktop}`);
        this.grid.classList.add(`mobile:card-grid--${mobile}`);
      }
    }
  );
}

/*
 * Note: the desktop filter show/hide toggle is now pure CSS (a checkbox +
 * :has() selector in plp.css). No JavaScript is involved, which makes it
 * immune to caching, timing and DOM-replacement issues.
 */
